import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import { useIntl } from 'react-intl';

import ButtonFormatted from 'components/atoms/ui/ButtonFormatted';
import { BUTTON_MAIN_TYPE } from 'constants/ui';
import pointsStyle from 'sass-boilerplate/stylesheets/components/launch/Points.module.scss';

const copyTextToClipboard = async (text: string) => {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const input = document.createElement('textarea');
  input.value = text;
  input.style.position = 'fixed';
  input.style.left = '-9999px';
  document.body.appendChild(input);
  input.select();
  const copied = document.execCommand('copy');
  document.body.removeChild(input);
  if (!copied) throw new Error('Clipboard unavailable');
};

const CustomPointsTooltip = ({ data, position, isVisible, setIsVisible, dataToCopy, setDataToCopy, colFiledData }) => {
  const { formatMessage } = useIntl();

  useEffect(() => {
    if (!isVisible) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsVisible(false);
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [isVisible, setIsVisible]);

  if (!isVisible) return null;

  const panelWidth = Math.min(360, window.innerWidth - 32);
  const left = Math.max(16, Math.min(position.x, window.innerWidth - panelWidth - 16));
  const above = position.y > window.innerHeight / 2;

  const copy = async (value: string) => {
    setDataToCopy(value);
    try {
      await copyTextToClipboard(value);
      setIsVisible(false);
    } catch (_) {
      toast.error(formatMessage({ id: 'error.msg.copyToClipborad' }));
    }
  };

  return createPortal(
    <div
      className={pointsStyle.tooltip}
      role="dialog"
      aria-label={formatMessage({ id: 'wall.user.details.information.title' })}
      style={{ top: above ? position.y - 10 : position.y + 10, left, transform: above ? 'translateY(-100%)' : undefined }}
      onMouseLeave={() => setIsVisible(false)}
    >
      <div className={pointsStyle.tooltipHeader}>
        <span>{formatMessage({ id: 'wall.user.details.information.title' })}</span>
        <button type="button" className={pointsStyle.tooltipClose}
          aria-label={formatMessage({ id: 'label.close.modal' })} onClick={() => setIsVisible(false)}>×</button>
      </div>
      <div className={pointsStyle.tooltipValue}>{colFiledData || '—'}</div>
      <div className={pointsStyle.tooltipActions}>
        <ButtonFormatted onClick={() => copy(colFiledData || '')} type={BUTTON_MAIN_TYPE.SECONDARY}
          buttonText="btn.label.copy" className={pointsStyle.tooltipAction} />
        <ButtonFormatted onClick={() => copy(JSON.stringify(data))} type={BUTTON_MAIN_TYPE.PRIMARY_INVERTED}
          buttonText="btn.label.copyRow" className={pointsStyle.tooltipAction} />
      </div>
    </div>,
    document.body
  );
};

export default CustomPointsTooltip;
